import { dpiVerdict, effectiveDpi, fitPrintSize } from "../../src/image/dpi.ts";
import type { DpiVerdict, SpotLimits } from "../../src/image/dpi.ts";
import { placement } from "../../src/image/placement.ts";
import type { SpotGeometry } from "../../src/image/placement.ts";
import { mergeLines } from "../../src/lib/items.ts";
import type { Offset } from "../../src/lib/offset.ts";
import { printTierLine } from "../../src/lib/estimate.ts";
import { priceOrder, printTierFor } from "../../src/lib/pricing.ts";
import type { OrderPrice, PriceItemInput } from "../../src/lib/pricing.ts";
import type { Fulfillment, GarmentType, PrintTierSetting, PublicPrices, Spot } from "../../src/lib/types.ts";

export const SLIDER_STEP = 0.25;
const onGrid = (n: number) => Math.floor(n / SLIDER_STEP + 1e-9) * SLIDER_STEP;

export function sliderBounds(spot: SpotLimits, aspect: number, printerMaxWidthIn: number): { min: number; max: number } {
  const max = Math.max(SLIDER_STEP, onGrid(fitPrintSize(aspect, 999, spot, printerMaxWidthIn).widthIn));
  return { min: Math.min(1, max), max };
}

// Starts at 80% of the largest width, stepping down until the logo prints at 300 DPI or the slider runs out.
export function defaultWidth(bounds: { min: number; max: number }, logoWidthPx: number): number {
  let width = Math.max(bounds.min, onGrid(bounds.max * 0.8));
  while (width - SLIDER_STEP >= bounds.min - 1e-9 && dpiVerdict(effectiveDpi(logoWidthPx, width)) !== "ok") width -= SLIDER_STEP;
  return Math.max(bounds.min, width);
}

export function printSize(spot: SpotLimits, aspect: number, widthIn: number, printerMaxWidthIn: number) {
  return fitPrintSize(aspect, widthIn, spot, printerMaxWidthIn);
}

export function dpiState(logoWidthPx: number, widthIn: number): { dpi: number; verdict: DpiVerdict } {
  const dpi = effectiveDpi(logoWidthPx, widthIn);
  return { dpi, verdict: dpiVerdict(dpi) };
}

// Whether the fix page may offer "print it anyway" for a logo under 150 DPI: an order whose fix-info did not say
// lowDpiAllowed: false (only a client's order may print that small, ruling R24; the server says so for every other
// order). The checkbox and Send both ask this.
export function lowDpiConsentAllowed(info: { storefrontId: string; lowDpiAllowed?: false }): boolean {
  return info.lowDpiAllowed !== false;
}

// The order page's sharpness note. canResize is false on the fix page, where the size is already set.
export function dpiNotice(dpi: number, verdict: DpiVerdict, canResize = true): { cls: "good" | "warn" | "bad"; text: string } {
  if (verdict === "ok") return { cls: "good", text: `Sharp at this size (${dpi} DPI).` };
  if (verdict === "warn") return { cls: "warn", text: `This will print a little soft at this size (${dpi} DPI). A larger file would be sharper.` };
  return { cls: "bad", text: `This logo is too small for this size (${dpi} DPI). ${canResize ? "Make it smaller or upload a larger file." : "Upload a larger file."}` };
}

// The logo's box on the photo in percentages of its natural size, moved by the placement's offset exactly as the server
// moves it (placement() rounds the offset to whole photo pixels).
export function previewBox(spot: SpotGeometry, ppi: number, widthIn: number, heightIn: number, photoW: number, photoH: number, offset?: Partial<Offset>) {
  const p = placement(spot, ppi, widthIn, heightIn, offset);
  const pct = (n: number, of: number) => `${((n / of) * 100).toFixed(3)}%`;
  return { left: pct(p.left, photoW), top: pct(p.top, photoH), width: pct(p.width, photoW), height: pct(p.height, photoH) };
}

export function cleanQuantities(q: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [size, qty] of Object.entries(q)) {
    const n = Math.floor(qty);
    if (Number.isFinite(n) && n > 0) out[size] = Math.min(n, 999);
  }
  return out;
}

export function cartTotals(lines: PriceItemInput[], prices: PublicPrices, fulfillment: Fulfillment): OrderPrice | null {
  if (lines.length === 0) return null;
  try {
    return priceOrder(lines, prices, fulfillment);
  } catch {
    return null;
  }
}

export interface CheckoutForm {
  name: string;
  email: string;
  phone: string;
  fulfillment: Fulfillment;
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
}

export function checkoutErrors(f: CheckoutForm): Record<string, string> {
  const e: Record<string, string> = {};
  if (!f.name.trim()) e.name = "Enter your name.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = "Enter a valid email address.";
  if (f.fulfillment === "ship") {
    if (!f.line1.trim()) e.line1 = "Enter a street address.";
    if (!f.city.trim()) e.city = "Enter a city.";
    if (!f.state.trim()) e.state = "Enter a state.";
    if (!f.zip.trim()) e.zip = "Enter a ZIP code.";
  }
  return e;
}

export interface CartLineShape {
  garmentId: string;
  garmentType: GarmentType;
  spot: Spot;
  widthIn: number;
  heightIn: number;
  quantities: Record<string, number>;
  unitPrices: Record<string, number> | null;
}

// The cart as the server stores it (src/lib/order-insert.ts): lines for the same garment with the same quantities are
// one item printed in more places, paid once. `first` is the group's earliest line in the cart, where its price shows.
export function cartGroups<T extends CartLineShape>(lines: T[]): { lines: T[]; first: T; input: PriceItemInput }[] {
  return mergeLines(lines).map((group) => {
    const [lead, ...rest] = group;
    return {
      lines: group,
      first: lines.find((l) => group.includes(l))!,
      input: {
        garmentType: lead!.garmentType, widthIn: lead!.widthIn, heightIn: lead!.heightIn, quantities: lead!.quantities,
        extraPrints: rest.map((r) => ({ widthIn: r.widthIn, heightIn: r.heightIn })),
        ...(lead!.unitPrices ? { unitCentsBySize: lead!.unitPrices } : {}),
      },
    };
  });
}

// A cart line's print tier as the buyer reads it ("Front center, medium: $8"), for a garment with an S&S cost, whose
// price is its garment plus a tier per print; null for a type-table garment or a print no tier holds (the server
// refuses that one).
export function cartPrintLine(line: Pick<CartLineShape, "spot" | "widthIn" | "heightIn" | "unitPrices">, tiers: PrintTierSetting[]): string | null {
  if (!line.unitPrices || Object.keys(line.unitPrices).length === 0) return null;
  try {
    return printTierLine(line.spot, printTierFor(line.widthIn, line.heightIn, tiers));
  } catch {
    return null;
  }
}
